import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getWorkflow, saveWorkflow } from "actions/workflows";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { EditorView } from "@codemirror/view";
import Input from "components/Elements/Input";
import ErrorField from "components/ErrorField";
import NiceModal from "@ebay/nice-modal-react";

// components
import Sidebar from "components/Sidebar/Sidebar.js";
// texts as props
import sidebar from "_texts/admin/sidebar/sidebar.js";
import Button from "components/Elements/Button";
import Select from "react-select";
import SelectElement from "components/Elements/Select";
import { createGlobalStyle } from "styled-components";

const GlobalStyle = createGlobalStyle`
  .selectedView {
    font-weight: bold;
  }

  .view {
    cursor: pointer;
  }

  .view:hover {
    text-decoration: underline;
  }
`;

const Workflow = () => {
  const { workflow_id } = useParams();
  const navigate = useNavigate();

  console.log("!!! WORKFLOW ID: ", workflow_id);
  const { workflow, savingWorkflow } = useSelector((state) => state.workflows);
  const { activeUser } = useSelector((state) => state.users);
  const [selectedWorkflowType, setSelectedWorkflowType] = useState(null);
  const [localWorkflow, setLocalWorkflow] = useState({
    id: "",
    public_id: "",
    name: "",
    description: "",
    workflow_type: "",
    input_params: '{ "input": { "type": "string" } }',
  });
  const [inputParametersJSON, setInputParametersJSON] = useState("");
  const [inputParameters, setInputParameters] = useState([]);
  const [inputParametersCount, setInputParametersCount] = useState(0);
  const [inputParametersView, setInputParametersView] = useState("fields");
  const [formErrors, setFormErrors] = useState({});
  const dispatch = useDispatch();
  const workflowTypes = [
    "training",
    "inference",
    "supporting content",
    "native app",
  ];

  useEffect(() => {
    if (workflow_id) {
      dispatch(getWorkflow(workflow_id));
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const input_params = {};
    inputParameters.forEach((param) => {
      input_params[param[0]] = { type: param[1] };
    });
    const input_params_json = JSON.stringify(input_params, null, 2);
    setInputParametersJSON(input_params_json);
  }, [inputParameters]);

  useEffect(() => {
    //dispatch(clearWorkflow());
    if (workflow) {
      window.history.replaceState(
        null,
        "Edit Workflow",
        `/workflow/${workflow.public_id}`
      );
      setSelectedWorkflowType({
        label: workflow.workflow_type,
        value: workflow.workflow_type,
      });
      setLocalWorkflow(workflow);
    } else if (!workflow_id) {
      window.history.replaceState(null, "New Workflow", "/workflow");
      setSelectedWorkflowType(null);
      setLocalWorkflow({
        id: "",
        public_id: "",
        name: "",
        description: "",
        workflow_type: "",
      });
    }

    // eslint-disable-next-line
  }, [workflow]);

  useEffect(() => {
    if (localWorkflow?.input_params) {
      const input_params = JSON.parse(localWorkflow.input_params);

      const keys = Object.keys(input_params);
      setInputParametersJSON(localWorkflow.input_params);
      setInputParametersCount(keys.length);
      setInputParameters(keys.map((key) => [key, input_params[key].type]));
    }
  }, [localWorkflow]);

  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleChange = ({ target }) => {
    const a = { ...localWorkflow };
    a[target.name] = target.value;
    setLocalWorkflow(a);
  };

  const handleCodeChange = (name, code) => {
    const a = { ...localWorkflow };
    a[name] = code;
    setLocalWorkflow(a);
  };

  const handleWorkflowTypeChange = (workflow_type) => {
    const a = { ...localWorkflow };
    setSelectedWorkflowType(workflow_type);
    a.workflow_type = workflow_type.value;
    setLocalWorkflow(a);
  };

  const validateFields = () => {
    setFormErrors({});
    const errors = {};

    const nameValid = localWorkflow.name && localWorkflow.name.length > 0;

    errors.name = nameValid ? "" : " is required";

    setFormErrors(errors);

    return nameValid;
  };

  const save = () => {
    if (validateFields()) {
      const saveLocalWorkflow = { ...localWorkflow };
      saveLocalWorkflow.input_params = inputParametersJSON;
      setLocalWorkflow(saveLocalWorkflow);
      dispatch(saveWorkflow(saveLocalWorkflow));
    }
  };

  const renderInputParameters = () => {
    const inputs = [];
    for (let i = 0; i < inputParametersCount; i++) {
      inputs.push(
        <div key={`input_${i}`}>
          <div style={{ display: "flex", flexDirection: "row" }} key={i}>
            <Input
              label={inputParameters[i][0]}
              type="text"
              name={`input_params_${i}`}
              value={inputParameters[i][0]}
              onChange={(e) => {
                const newParams = [...inputParameters];
                newParams[i][0] = e.target.value;
                setInputParameters(newParams);
              }}
              style={{ width: "350px", marginRight: "10px" }}
            />
            <SelectElement
              label="Type"
              placeholder="Type"
              name={`input_params_${i}_type`}
              defaultValue={inputParameters[i][1]}
              items={[
                "string",
                "number",
                "boolean",
                "object",
                "array",
                "doclist",
              ]}
              onChange={(value) => {
                const newParams = [...inputParameters];
                newParams[i][1] = value;
                setInputParameters(newParams);
              }}
            />
          </div>
          {inputParameters[i][1] === "array" && <div> ARRAY </div>}
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <div style={{ width: "350px", marginRight: "10px" }}>Key</div>
          <div>Type</div>
        </div>
        {inputs}
      </div>
    );
  };

  return (
    <>
      <GlobalStyle />
      <Sidebar {...sidebar} />
      <div
        className="relative md:ml-64 bg-blueGray-100 pt-24"
        style={{ height: "100%", minHeight: "100vh", zIndex: 30 }}
      >
        <div className="px-4 md:px-6 mx-auto w-full -mt-24 pt-24">
          <div className="flex flex-wrap">
            <div className="relative p-6 flex-auto ">
              <div>
                <div
                  onClick={() => {
                    navigate("/admin/workflows");
                  }}
                >
                  <i className="fa fa-arrow-left" /> Back to Workflows
                </div>{" "}
              </div>
              <h3 className="font-bold text-lg text-blueGray-700">
                {workflow && workflow.id && (
                  <>
                    {localWorkflow.name}
                    <Button
                      style={{ marginLeft: "10px" }}
                      onClick={() => {
                        navigate(`/admin/workflow/${workflow.public_id}/graph`);
                      }}
                    >
                      Graph
                    </Button>
                    <Button
                      style={{ marginLeft: "10px" }}
                      onClick={() =>
                        NiceModal.show("test-workflow", {
                          workflow_id: localWorkflow.id,
                        })
                      }
                    >
                      Run
                    </Button>
                  </>
                )}
              </h3>

              <div className="my-4 text-blueGray-500 text-lg leading-relaxed">
                <label>ID</label>
                <Input
                  label="ID"
                  placeholder="ID"
                  name="id"
                  value={localWorkflow.id}
                  disabled
                />
                <label>Public ID</label>
                <Input
                  label="Public ID"
                  placeholder="Public ID"
                  name="public_id"
                  value={localWorkflow.public_id}
                  disabled
                />
                <label>
                  Name <ErrorField>{formErrors.name}</ErrorField>
                </label>
                <Input
                  label="Name"
                  placeholder="Name"
                  name="name"
                  value={localWorkflow.name}
                  onChange={handleChange}
                />
                <label>
                  Type <ErrorField>{formErrors.workflow_type}</ErrorField>
                </label>
                <Select
                  label="Workflow Type"
                  placeholder="Choose a Workflow Type"
                  name="type"
                  value={selectedWorkflowType}
                  options={workflowTypes.map((t) => {
                    return { label: t, value: t };
                  })}
                  onChange={handleWorkflowTypeChange}
                />
                <label>
                  Description <ErrorField>{formErrors.description}</ErrorField>
                </label>
                <Input
                  type="textarea"
                  label="Description"
                  placeholder="Description"
                  name="description"
                  value={localWorkflow.description}
                  onChange={handleChange}
                  style={{ height: "100px" }}
                />
                <label>
                  Input Parameters (
                  <span
                    className={
                      inputParametersView === "fields" ? "selectedView" : "view"
                    }
                    style={{ marginLeft: "5px", marginRight: "5px" }}
                    onClick={() => setInputParametersView("fields")}
                  >
                    Fields
                  </span>{" "}
                  |{" "}
                  <span
                    className={
                      inputParametersView === "json" ? "selectedView" : "view"
                    }
                    style={{ marginLeft: "5px", marginRight: "5px" }}
                    onClick={() => setInputParametersView("json")}
                  >
                    JSON
                  </span>
                  )<ErrorField>{formErrors.input_params}</ErrorField>
                </label>
                <CodeMirror
                  extensions={[json(), EditorView.lineWrapping]}
                  name="input_params"
                  value={inputParametersJSON}
                  onChange={(code) => handleCodeChange("input_params", code)}
                  style={{
                    width: "calc(100vw - 350px)",
                    display: inputParametersView === "json" ? "block" : "none",
                  }}
                />
                <div
                  style={{
                    display:
                      inputParametersView === "fields" ? "block" : "none",
                  }}
                >
                  {renderInputParameters()}
                </div>
              </div>
              {activeUser && activeUser.user_type === "admin" && (
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <button
                    className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={save}
                    disabled={savingWorkflow}
                  >
                    {savingWorkflow ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin"></i>{" "}
                        Saving...
                      </>
                    ) : (
                      <>Save Changes</>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Workflow;
